/**
 * Comptabilité de la vue d'aigle — `/v1/console/comptabilite` (ARTO-58).
 *
 * Les recettes de la plateforme par mois et par service (HT, TVA, TTC), et
 * leur RÉPARTITION entre les parties prenantes du partage (`platform_partners`)
 * selon une grille réglable par service. Ce fichier porte les schémas
 * d'entrée et la forme des réponses : la console l'importe sans tirer aucune
 * dépendance serveur. Les calculs vivent dans l'API
 * (`console/pilotage/comptabilite-calcul.ts`).
 *
 * Ce qui ne se discute pas ici :
 *
 *   - AUCUN TARIF, AUCUN POURCENTAGE N'EST ÉCRIT DANS LE CODE. Un montant à
 *     saisir (`null`) n'entre dans aucun total ; il est compté (`aSaisir`). Les
 *     parts sont celles que la direction saisit ; un service sans version de
 *     grille applicable n'est pas réparti (`nonReparti`), jamais réparti « au
 *     mieux » ;
 *   - LA TVA NE SE PARTAGE PAS : elle est collectée pour l'État. La
 *     répartition porte sur le HT ;
 *   - montants en centimes entiers, parts en points de base (10 000 = 100 %) ;
 *   - un mois CLÔTURÉ se lit dans son instantané, jamais recalculé ;
 *   - un bénéficiaire peut EXCLURE une galerie (`galeriesExclues`, à partir
 *     d'un mois) : sur les recettes de cette galerie, sa part revient au
 *     bénéficiaire qui reçoit le reste (`recoitLeReste`) ; les autres gardent
 *     la leur. Sans bénéficiaire du reste, le service n'est pas réparti ce
 *     mois-là — jamais une part inventée.
 */
import { z } from 'zod';
import { centimesPositifsSchema, idSchema, POINTS_DE_BASE } from './commun.js';
import { SAISIES_MONTANT } from './comptabilite-calcul.js';
import { champsSuiviPlateforme, moisSchema, TAUX_TVA_RECETTE_PAR_DEFAUT, tauxTvaRecetteSchema, typeRecetteIdSchema, } from './pilotage.js';
/** La fenêtre la plus large d'une lecture de la comptabilité, en mois. */
export const MOIS_MAX_COMPTABILITE = 12;
/** L'état d'un mois : recalculé à chaque lecture, ou figé par sa clôture. */
export const STATUTS_MOIS_COMPTABLE = ['provisoire', 'cloture'];
// ── Grille de répartition — `platform_revenue_split_rules` ──────────────────
/** La part d'un bénéficiaire du partage dans une version de la grille. */
export const partRepartitionSchema = z
    .object({
    beneficiaireId: idSchema,
    partPointsDeBase: z.int().min(0).max(POINTS_DE_BASE),
})
    .strict();
/** Assez pour tous les associés imaginables ; borne la taille d'un corps. */
const parts = z.array(partRepartitionSchema).min(1).max(50);
/**
 * Une VERSION de la grille d'un service, applicable à partir d'un mois : la
 * version d'un mois est celle de plus grand `depuisMois` qui ne le dépasse
 * pas. Changer un pourcentage, c'est créer une version à partir d'un mois :
 * les mois antérieurs gardent l'ancienne.
 *
 * Strict : la somme exacte des parts (10 000), l'unicité et l'existence des
 * bénéficiaires sont contrôlées par la route (`defautDesParts`, 422), qui voit
 * la base.
 */
const champsRegle = z.object({
    /** Un service de base ou un type de recette créé : jamais un libellé. */
    service: typeRecetteIdSchema,
    depuisMois: moisSchema,
    parts,
});
export const regleRepartitionCreationSchema = champsRegle.strict();
/** Corriger une version : son mois de départ, ses parts — jamais son service. */
export const regleRepartitionMiseAJourSchema = z
    .object({ depuisMois: moisSchema.optional(), parts: parts.optional() })
    .strict();
/** Le document stocké. */
export const regleRepartitionSchema = champsRegle.extend(champsSuiviPlateforme);
/**
 * Les parts d'une version somment EXACTEMENT 10 000 points de base, et chaque
 * bénéficiaire y figure au plus une fois. Une somme inférieure laisserait des
 * centimes sans destinataire ; supérieure, elle en distribuerait qui
 * n'existent pas.
 */
export function defautDesParts(liste) {
    const vus = new Set();
    for (const part of liste) {
        if (vus.has(part.beneficiaireId)) {
            return { raison: 'beneficiaire_en_double', beneficiaireId: part.beneficiaireId };
        }
        vus.add(part.beneficiaireId);
    }
    const somme = liste.reduce((total, part) => total + part.partPointsDeBase, 0);
    return somme === POINTS_DE_BASE ? null : { raison: 'somme_parts', somme };
}
// ── Exclusions de galeries — `platform_partners.galeriesExclues` ─────────────
/**
 * Exclure une galerie de la part d'un bénéficiaire, à partir d'un mois : sur
 * les recettes de cette galerie (abonnement, fraction d'un forfait, revenu
 * ponctuel rattaché), sa part va au bénéficiaire qui reçoit le reste.
 *
 * Strict : l'existence de la galerie, son statut (pas un espace de test), le
 * bénéficiaire (pas celui du reste) et les mois clôturés sont contrôlés par la
 * route, qui voit la base.
 */
export const exclusionGalerieCreationSchema = z
    .object({ galerieId: idSchema, depuisMois: moisSchema })
    .strict();
// ── Types de recette — `platform_revenue_types` ──────────────────────────────
/**
 * Le libellé d'un type de recette créé : le français, l'anglais facultatif
 * (l'écran retombe sur le français). Un libellé n'est jamais une clé : le
 * type se désigne par son identifiant, et le renommer ne touche à rien d'autre.
 */
export const libelleTypeRecetteSchema = z
    .object({
    fr: z.string().trim().min(1).max(80),
    en: z.string().trim().min(1).max(80).optional(),
})
    .strict();
const champsTypeRecette = z.object({
    libelle: libelleTypeRecetteSchema,
    /** Le rang du type dans l'écran, après les cinq services de base. */
    ordre: z.int().min(0).max(9_999),
    /** Un type archivé ne reçoit plus d'encaissement ; ses mois passés comptent toujours. */
    actif: z.boolean(),
    /**
     * Le montant HT que le type doit atteindre, en centimes (une contribution
     * versée en plusieurs fois) ; `null` = aucun. Direction seule.
     */
    objectifHtCentimes: z.int().min(1).nullable(),
});
/** Un type naît actif ; son ordre vaut 0 et il n'a pas d'objectif, sauf mention. */
export const typeRecetteCreationSchema = champsTypeRecette
    .omit({ actif: true })
    .extend({
    ordre: champsTypeRecette.shape.ordre.default(0),
    objectifHtCentimes: champsTypeRecette.shape.objectifHtCentimes.default(null),
})
    .strict();
/** Renommer, réordonner, archiver ou rétablir, fixer l'objectif. */
export const typeRecetteMiseAJourSchema = champsTypeRecette.partial().strict();
// ── Encaissements — `platform_revenues` ──────────────────────────────────────
/** Un jour du calendrier, `AAAA-MM-JJ`, qui existe. */
export const jourSchema = z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'Jour attendu : AAAA-MM-JJ.')
    .refine((jour) => {
    const [annee, mois, quantieme] = jour.split('-').map(Number);
    return new Date(Date.UTC(annee, mois - 1, quantieme)).getUTCDate() === quantieme;
}, { message: 'Ce jour n’existe pas.' });
/**
 * Une somme REÇUE, saisie par la direction : un revenu ponctuel daté, d'un
 * type de recette, d'un payeur. Il est rangé avec les revenus — le calcul,
 * la clôture et le verrou des mois s'appliquent tels quels.
 *
 *   - `encaisseLe` : le jour de l'encaissement, `AAAA-MM-JJ` (un jour à
 *     Paris), ou un instant, ramené au jour de Paris. Le mois comptable en
 *     découle, jamais saisi à part ;
 *   - `saisie` : `montantCentimes` est le HT (`ht`) ou le TTC (`ttc`) reçu.
 *     Le serveur en tire HT, TVA et TTC (`montantsRecette`) ;
 *   - `service` : obligatoire, un service de base ou un type créé ACTIF ;
 *   - `payeur` et `libelle` : du texte libre, que seule la direction lit.
 *     Sans libellé, celui du type.
 */
/**
 * Le plus grand montant d'UN encaissement : un milliard d'euros, en centimes.
 * Bien au-delà de toute somme réelle, il garde chaque produit du calcul exact
 * en nombres flottants (2^53 ≈ 9 × 10^15) : `HT × part` de la répartition et
 * `HT × taux` de la TVA, parts et taux au plus 10 000 points, restent sous
 * 10^15. Le HT d'un TTC se calcule, lui, en entiers longs (`htDepuisTtc`).
 */
export const MONTANT_ENCAISSEMENT_MAX_CENTIMES = 100_000_000_000;
const champsEncaissement = z.object({
    encaisseLe: z.union([
        jourSchema,
        z.union([z.date(), z.iso.datetime({ offset: true })]).pipe(z.coerce.date()),
    ]),
    saisie: z.enum(SAISIES_MONTANT),
    montantCentimes: centimesPositifsSchema.min(1).max(MONTANT_ENCAISSEMENT_MAX_CENTIMES),
    tauxTvaPointsDeBase: tauxTvaRecetteSchema,
    service: typeRecetteIdSchema,
    payeur: z.string().trim().min(1).max(120).nullable(),
    galerieId: idSchema.nullable(),
    libelle: z.string().trim().min(1).max(160).nullable(),
});
export const encaissementCreationSchema = champsEncaissement
    .extend({
    saisie: champsEncaissement.shape.saisie.default('ht'),
    tauxTvaPointsDeBase: tauxTvaRecetteSchema.default(TAUX_TVA_RECETTE_PAR_DEFAUT),
    payeur: champsEncaissement.shape.payeur.default(null),
    galerieId: champsEncaissement.shape.galerieId.default(null),
    libelle: champsEncaissement.shape.libelle.default(null),
})
    .strict();
/**
 * Corriger un encaissement : tout champ, le serveur recalcule. Sans
 * `montantCentimes`, changer de `saisie` reprend le HT (vers `ht`) ou le TTC
 * (vers `ttc`) déjà connu ; changer le taux d'une saisie TTC garde le TTC.
 */
export const encaissementMiseAJourSchema = champsEncaissement.partial().strict();
/**
 * D'où vient la répartition d'un encaissement que la liste sert :
 *
 *   - `provisoire` : son mois est ouvert — la grille et le partage
 *     d'aujourd'hui, comme l'aperçu ;
 *   - `figee` : son mois est clôturé — celle que la clôture a figée, que le
 *     partage ait changé depuis ou non ;
 *   - `figee_non_detaillee` : son mois a été clôturé avant que la clôture
 *     fige la répartition de chaque encaissement. Aucune part n'est servie
 *     (`repartition: null`) plutôt qu'une part recalculée, peut-être fausse :
 *     celles du mois clôturé font foi ;
 *   - `exclue_espace_de_test` : sa galerie est un espace de test — marquée
 *     après la saisie —, et le mois l'exclut de ses comptes : aucune part
 *     (`repartition: null`). Au mois ouvert, l'état d'aujourd'hui ; au mois
 *     clôturé, celui de la clôture (la ligne manque au mois figé).
 */
export const ETATS_REPARTITION_ENCAISSEMENT = [
    'provisoire',
    'figee',
    'figee_non_detaillee',
    'exclue_espace_de_test',
];
//# sourceMappingURL=comptabilite.js.map